import { supabase, subscribeToChannel } from "./supabaseClient";

export const productsRepository = {
  list: (tenantId: string) =>
    supabase
      .from("products")
      .select(
        `
        *,
        category:categories(id, name),
        variation_attributes:product_variation_attributes(*),
        variations:product_variations(
          *,
          attribute_links:product_variation_attribute_links(attribute_id)
        )
      `
      )
      .eq("tenant_id", tenantId)
      .is("archived_at", null)
      .order("created_at", { ascending: false })
      .order("position", { ascending: true, referencedTable: "product_variation_attributes" })
      .order("value_position", { ascending: true, referencedTable: "product_variation_attributes" })
      .limit(1000),

  listPaginated: (tenantId: string, opts: { page: number; pageSize: number; search?: string; categoryId?: string }) => {
    const { page, pageSize, search, categoryId } = opts;
    let query = supabase
      .from("products")
      .select(
        `id, name, product_code, sale_price, cost_price, stock, has_variations,
        image_urls, archived_at, category_id,
        category:categories(id, name),
        variations:product_variations(id, stock)`,
        { count: "exact" }
      )
      .eq("tenant_id", tenantId)
      .is("archived_at", null)
      .order("created_at", { ascending: false });

    if (search) {
      const s = search.replace(/[,.()]/g, "");
      query = query.or(`name.ilike.%${s}%,product_code.ilike.%${s}%`);
    }
    if (categoryId && categoryId !== "all") {
      query = query.eq("category_id", categoryId);
    }

    return query.range(page * pageSize, (page + 1) * pageSize - 1);
  },

  getById: (tenantId: string, productId: string) =>
    supabase
      .from("products")
      .select(
        `*, category:categories(id, name),
        variation_attributes:product_variation_attributes(*),
        variations:product_variations(*,
          attribute_links:product_variation_attribute_links(attribute_id))`
      )
      .eq("tenant_id", tenantId)
      .eq("id", productId)
      .order("position", { ascending: true, referencedTable: "product_variation_attributes" })
      .order("value_position", { ascending: true, referencedTable: "product_variation_attributes" })
      .single(),

  create: (tenantId: string, data: Record<string, unknown>) =>
    supabase
      .from("products")
      .insert({ ...data, tenant_id: tenantId })
      .select()
      .single(),

  update: (tenantId: string, id: string, data: Record<string, unknown>) =>
    supabase.from("products").update(data).eq("tenant_id", tenantId).eq("id", id).select().single(),

  archive: async (tenantId: string, id: string) => {
    const { error } = await supabase.rpc("atomic_archive_product", {
      p_product_id: id,
      p_tenant_id: tenantId,
    });
    return { data: null, error };
  },

  checkCodeExists: (tenantId: string, code: string, excludeId?: string) => {
    let query = supabase
      .from("products")
      .select("id")
      .eq("tenant_id", tenantId)
      .eq("product_code", code.toUpperCase())
      .is("archived_at", null);
    if (excludeId) {
      query = query.neq("id", excludeId);
    }
    return query;
  },

  checkCodesExist: (tenantId: string, codes: string[], excludeId?: string) => {
    let query = supabase
      .from("products")
      .select("product_code")
      .eq("tenant_id", tenantId)
      .in("product_code", codes.map((c) => c.toUpperCase()))
      .is("archived_at", null);
    if (excludeId) {
      query = query.neq("id", excludeId);
    }
    return query;
  },

  subscribe: (tenantId: string, callback: () => void) =>
    subscribeToChannel(
      `products-realtime-${tenantId}`,
      "products",
      `tenant_id=eq.${tenantId}`,
      ["INSERT", "UPDATE", "DELETE"],
      callback
    ),
};

export const variationAttributesRepository = {
  createBatch: (
    attributes: Array<{
      product_id: string;
      attribute_type: string;
      attribute_value: string;
      position: number;
    }>
  ) =>
    supabase
      .from("product_variation_attributes")
      .insert(attributes)
      .select(),

  deleteForProduct: (_tenantId: string, productId: string) =>
    supabase
      .from("product_variation_attributes")
      .delete()
      .eq("product_id", productId),
};

export const variationsRepository = {
  createBatch: (
    variations: Array<{
      product_id: string;
      stock: number;
    }>
  ) => supabase.from("product_variations").insert(variations).select(),

  createAttributeLinks: (
    links: Array<{
      variation_id: string;
      attribute_id: string;
      tenant_id: string;
    }>
  ) => supabase.from("product_variation_attribute_links").insert(links),

  deleteForProduct: (_tenantId: string, productId: string) =>
    supabase
      .from("product_variations")
      .delete()
      .eq("product_id", productId),
};
